import { Component, HostListener, OnInit, OnDestroy } from '@angular/core'
import { AuthService } from './services/auth.service'
import { NzMessageService } from 'ng-zorro-antd/message'
import { UsersService } from './services/users.service'
import { getImageUrl } from '../environments/environment'
import { BreakpointObserver } from '@angular/cdk/layout'
import { Router, NavigationEnd } from '@angular/router'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { NavigationService } from './core/navigation/navigation.service'
import { AUTH_STORAGE_KEYS, AdminPrefsService } from './core/navigation/admin-prefs.service'
import { NavGroup, NavItem, NavLeaf, isNavGroup } from './core/navigation/nav.model'
import { NavMatch } from './core/navigation/navigation.service'
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  getImageUrl(path: string | null | undefined): string {
    return getImageUrl(path)
  }
  isCollapsed = false
  isMobile = false
  isAuthenticated!: boolean
  /** Which sections are expanded. Everything else stays shut, which is the point. */
  private openGroups = new Set<string>()
  paletteVisible = false
  /** Where the current route sits in the menu — the header breadcrumb trail. */
  breadcrumb: NavMatch | null = null
  adminName = ''
  /** Cached rather than read through a getter, which would re-resolve every cycle. */
  favorites: NavLeaf[] = []

  constructor(
    public authService: AuthService,
    public nav: NavigationService,
    private prefs: AdminPrefsService,
    private msg: NzMessageService,
    private usersService: UsersService,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
  ) {}

  /**
   * `*ngIf="asLeaf(item) as leaf"` is how the template narrows a NavItem — a bare
   * `isNavGroup(item)` check in a template does not narrow the type, and strict
   * template checking then rejects `item.route`.
   */
  asLeaf(item: NavItem): NavLeaf | null {
    return isNavGroup(item) ? null : item
  }

  /** A group with no route of its own: an expandable section. */
  asGroup(item: NavItem): NavGroup | null {
    return isNavGroup(item) && !item.route ? item : null
  }

  /**
   * A group that is also a destination — a tabbed hub. One sidebar row, with its
   * children reachable as tabs rather than as a submenu.
   */
  asHub(item: NavItem): NavGroup | null {
    return isNavGroup(item) && item.route ? item : null
  }

  /**
   * Ctrl+K / Cmd+K opens the palette from anywhere.
   *
   * Ignored while typing so the shortcut cannot steal a keystroke from a form —
   * every admin page is a table filter or a create/edit form.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isAuthenticated) return
    if (event.key !== 'k' && event.key !== 'K') return
    if (!event.ctrlKey && !event.metaKey) return
    event.preventDefault()
    this.paletteVisible = !this.paletteVisible
  }

  refreshFavorites(): void {
    this.favorites = this.nav.favorites
  }

  isGroupOpen(groupId: string): boolean {
    return this.openGroups.has(groupId)
  }

  /** Only an explicit toggle is persisted — auto-expanding the active section is not a preference. */
  setGroupOpen(groupId: string, open: boolean): void {
    if (open) {
      this.openGroups.add(groupId)
    } else {
      this.openGroups.delete(groupId)
    }
    this.prefs.setGroupOpen(groupId, open)
  }

  /**
   * Everything that depends on "where am I": the breadcrumb, which section is
   * expanded, and the recents list.
   */
  private syncRoute(url: string): void {
    this.breadcrumb = this.nav.findByUrl(url)
    // Land on a page and its section opens around it, so the admin can see where
    // they are without hunting for the right collapsed header.
    this.breadcrumb?.path.forEach((group) => this.openGroups.add(group.id))
    this.nav.recordVisit(url)
  }

  ngOnInit() {
    this.isAuthenticated = this.authService.isAuthenticated()
    if (this.isAuthenticated) {
      this.getUserDetails()
      this.prefs.prefs.openGroups.forEach((id) => this.openGroups.add(id))
      this.syncRoute(this.router.url)
      this.refreshFavorites()
    }

    this.breakpointObserver
      .observe('(max-width: 768px)')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches
        if (this.isMobile) {
          this.isCollapsed = true
        }
      })

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.syncRoute(this.router.url)
        if (this.isMobile) {
          this.isCollapsed = true
        }
      })
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  logout() {
    // Not localStorage.clear() — that would also wipe this admin's saved
    // preferences, which are keyed per user and meant to outlive the session.
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
    this.nav.reset()
    this.msg.success('تم تسجيل الخروج بنجاح')
    setTimeout(() => {
      window.location.reload()
    }, 500)
  }

  getUserDetails() {
    this.usersService.viewUser().subscribe((res: any) => {
      localStorage.setItem('roles', res.user.roles || [])
      localStorage.setItem('accessControls', JSON.stringify(res.user.accessControlId?.pages || []))
      this.adminName =
        [res.user.firstName, res.user.lastName].filter(Boolean).join(' ') || res.user.email || ''
    })
  }
}
